import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ChevronUp, ListOrdered, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppHeader, Wordmark } from '@/components/layout/AppHeader';
import { cn } from '@/lib/utils';
import { getProfessorToken } from '../authCookie';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface PreviewClass {
  code: string;
  name: string;
  joinCode: string;
  questions: { text: string; votes: number }[];
}

const previewClasses: PreviewClass[] = [
  {
    code: 'CPSC 4390',
    name: 'Software Engineering',
    joinCode: 'FZB267',
    questions: [
      { text: 'Could you explain test driven development again?', votes: 18 },
      { text: 'Will our groups be randomized?', votes: 11 },
      { text: 'How popular is the MERN stack in the real world?', votes: 6 },
    ],
  },
  {
    code: 'ECON 1115',
    name: 'Intro Microeconomics',
    joinCode: 'MKT4U2',
    questions: [
      { text: 'Could you go a bit slower?', votes: 18 },
      { text: 'Can you walk through consumer surplus on the graph one more time?', votes: 11 },
      { text: 'Why does a price ceiling create a shortage instead of just lowering prices?', votes: 6 },
    ],
  },
  {
    code: 'MCDB 3000',
    name: 'Biochemistry',
    joinCode: 'ATP7X9',
    questions: [
      { text: 'What’s the difference between Km and Kcat?', votes: 15 },
      { text: 'Will we need to know every intermediate in the citric acid cycle for the exam?', votes: 12 },
      { text: 'How many ATPs are consumed in glycolysis and cellular respiration?', votes: 5 },
    ],
  },
  {
    code: 'MATH 1200',
    name: 'Calculus of Functions of Several Variables',
    joinCode: 'GRD3NT',
    questions: [
      { text: 'When do we use Lagrange multipliers versus just setting the gradient to zero?', votes: 13 },
      { text: 'Can you redo the divergence theorem example with the sphere?', votes: 9 },
      { text: 'Does the order of integration ever change the answer?', votes: 4 },
    ],
  },
  {
    code: 'CPSC 2230',
    name: 'Data Structures and Programming Techniques',
    joinCode: 'HLL456',
    questions: [
      { text: 'How do we use the hash table in the context of the course?', votes: 14 },
      { text: 'Why does the recursive version use O(n) stack space?', votes: 9 },
      { text: 'Will the midterm cover hash collisions?', votes: 4 },
    ],
  },
];

const ROTATE_MS = 6000;

const steps = [
  {
    icon: <MessageSquare />,
    title: 'Ask without raising a hand',
    body: 'Students post questions anonymously from any device. No accounts to create, just a class code.',
  },
  {
    icon: <ChevronUp />,
    title: 'Upvote what matters',
    body: 'The room votes, so the questions the most people share float to the top on their own.',
  },
  {
    icon: <ListOrdered />,
    title: 'Answer in order',
    body: 'Professors see a live, ranked list, mark questions answered, and get a summary when class ends.',
  },
];

function DashboardPreview() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % previewClasses.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [paused]);

  const cls = previewClasses[index];

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="overflow-hidden rounded-2xl border bg-card shadow-card">
        <div key={cls.code} className="animate-in fade-in duration-500">
          <div className="flex items-center justify-between border-b px-5 py-3.5">
            <div className="flex min-w-0 items-center gap-3">
              <span className="truncate text-sm font-semibold">
                {cls.code} · {cls.name}
              </span>
              <span className="hidden shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground sm:inline-flex">
                <span className="h-2 w-2 rounded-full bg-success" />
                Live
              </span>
            </div>
            <span className="shrink-0 rounded-full border px-3 py-1 text-xs font-semibold tracking-widest">{cls.joinCode}</span>
          </div>
          <ul className="divide-y">
            {cls.questions.map((q, i) => (
              <li key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="flex w-10 shrink-0 flex-col items-center text-muted-foreground">
                  <ChevronUp className="h-4 w-4" aria-hidden />
                  <span className="text-sm font-semibold text-foreground">{q.votes}</span>
                </div>
                <p className="flex-1 text-sm leading-relaxed">{q.text}</p>
                <span className="hidden shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold sm:inline-flex">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  Mark answered
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Example classes">
        {previewClasses.map((c, i) => (
          <button
            key={c.code}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Show ${c.code}`}
            onClick={() => setIndex(i)}
            className={cn(
              'h-1.5 rounded-full transition-all',
              i === index ? 'w-6 bg-foreground' : 'w-1.5 bg-border hover:bg-muted-foreground'
            )}
          />
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const token = getProfessorToken();
    if (!token) {
      setChecked(true);
      return;
    }
    let cancelled = false;
    fetch(`${backendUrl}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) navigate('/professor', { replace: true });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => { cancelled = true; };
  }, [navigate]);

  if (!checked) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppHeader
        right={
          <>
            <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => navigate('/professor')}>
              Professor sign in
            </Button>
            <Button className="rounded-full" onClick={() => navigate('/join')}>
              Join a class
            </Button>
          </>
        }
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="container pb-16 pt-20 sm:pt-28">
          <div className="max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-brand-text">
              Live Lecture Platform
            </p>
            <h1 className="text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-6xl">
              A lot goes unsaid during lectures.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            Some students are lost but can't articulate the question. Others are bored and won't say so. With lectureme, students can ask questions anonymously and signal what's working and what isn't, live.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button variant="brand" size="lg" className="rounded-full px-7" onClick={() => navigate('/join')}>
                Join a class
                <ArrowRight aria-hidden />
              </Button>
              <Button variant="outline" size="lg" className="rounded-full px-7" onClick={() => navigate('/professor')}>
                I'm a professor
              </Button>
            </div>
          </div>
        </section>

        {/* Product preview */}
        <section className="container pb-24">
          <DashboardPreview />
        </section>

        {/* How it works */}
        <section className="border-t">
          <div className="container py-20">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">How it works</h2>
            <div className="mt-10 grid gap-10 sm:grid-cols-3">
              {steps.map((step) => (
                <div key={step.title}>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-muted [&_svg]:h-5 [&_svg]:w-5">
                    {step.icon}
                  </div>
                  <h3 className="text-base font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t bg-muted/50">
        <div className="container flex flex-col items-start justify-between gap-6 py-10 sm:flex-row sm:items-center">
          <div>
            <Wordmark />
            <p className="mt-1 text-sm text-muted-foreground">Elevating lectures.</p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <a href="mailto:neil.mathew@yale.edu" className="inline-flex items-center gap-2 hover:text-foreground">
              <Mail className="h-4 w-4" aria-hidden />
              Neil Mathew
            </a>
            <a href="mailto:bukhosi.dube@yale.edu" className="inline-flex items-center gap-2 hover:text-foreground">
              <Mail className="h-4 w-4" aria-hidden />
              Bukhosi Dube
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
